<template>
  <main class="container container-trip-search d-flex flex-column">
    <CheckBoxComponent @onClick="isRoundTrip" :state="roundTrip" :id="'roundTrip'" name="roundTrip"
                       class="checkbox-default mb-4">
      <template #checkbox-text>
        <label class="ml-5" for="roundTrip">{{ $t('round_trip') }}</label>
      </template>
    </CheckBoxComponent>

    <TransportationComponent :round-trip="roundTrip" @on-search-submit="onSearchSubmit"/>
    <span class="custom-route-error-color font-small" v-for="error in backEndErrorMessage" :key="error">
      {{ $t(error) }}
    </span>

    <div id="main-transportation-title" class="d-flex flex-column align-items-center justify-content-center">
      <div class="font-big mt-5 transportation-title">{{ $t('passenger_transportation') }}</div>
      <h3 class="font-weight-600">{{ pickUp.address_to_show }}, {{ pickUp.city }} - {{ dropOff.address_to_show }}, {{ dropOff.city }}</h3>
    </div>

    <div class="transportation-tickets-container row row-cols-2">
      <TransportationTicketComponent
          :is-forward-ticket="true"
          :display-props="{ imgUrl: '/images/next-arrow.svg', placeholder: 'choose_ticket_forward' }"
          :forward-ticket="forwardTicket"
      />
      <TransportationTicketComponent
          :is-forward-ticket="false"
          :display-props="{ imgUrl: '/images/previous-arrow.svg', placeholder: 'choose_ticket_back' }"
          :back-ticket="backTicket"
      />
    </div>

    <DatesSliderComponent
        :how-many-to-load="6"
        :selected-date="tripDepartureDate.toISOString()"
        :round-trip="roundTrip"
        @selected-date="onSelectDate($event)"
    />

    <div class="mt-5 d-flex flex-column align-items-start">
      <AdditionalInfoCheckboxModal
          :id="'additional-pickup-info'"
          :upperText="$t('pickup_from_the_specified_address')"
          :lowerText="$t('enter_a_pickup_address')"
          :labelText="$t('pick_up_from_the_specified_address')"
          :init_is_checked="specifiedForwardTicketData.pickup_is_checked"
          :init_address="specifiedForwardTicketData.pickup_specified_address"
          :init_note="specifiedForwardTicketData.pickup_note"
          @changed-address="onChangeAddress($event)"
          @changed-note="onChangeNote($event)">
      </AdditionalInfoCheckboxModal>
      <AdditionalInfoCheckboxModal
          class="mt-2"
          :id="'additional-dropoff-info'"
          :upperText="$t('dropoff_at_specified_address')"
          :lowerText="$t('enter_dropoff_address')"
          :labelText="$t('drop_off_at_specified_address')"
          :init_is_checked="specifiedForwardTicketData.dropoff_is_checked"
          :init_address="specifiedForwardTicketData.dropoff_specified_address"
          :init_note="specifiedForwardTicketData.dropoff_note"
          @changed-address="onChangeAddress($event)"
          @changed-note="onChangeNote($event)">
      </AdditionalInfoCheckboxModal>
    </div>

    <!--looping available transportations-->
    <div class="transportation-routes-list-container d-flex flex-column align-items-center">
      <template v-if="isLoading">
        <div class="is-loading"></div>
      </template>
      <template v-else>
        <template v-for="transportation in transportations" :key="transportation.id">
          <TransportationItemComponent
              :map-center="mapCenter"
              :placeholder="roundTrip ? 'select' : 'order_now'"
              :is-round-trip="roundTrip"
              :transportation="transportation"
              @select-transportation="onSelectTransportation($event)">
          </TransportationItemComponent>
        </template>
      </template>
    </div>
  </main>
</template>

<script lang="ts">
import {defineComponent} from "vue";
import {mapState, useStore} from "vuex";
import axios from "axios";
import {routesConst} from "../../routes";
import apiService from "../../services/apiService";
import apiTransportationsService from "../../services/apiTransportationsService";
import apiGoogleService from "../../services/apiGoogleService";

import type {IOrderTrueRoundTripState} from "@/store/states/IOrderTrueRoundTripState.interface";
import type {Coords} from "@/store/states/ICoordinatesState.interface";
import type {IOrderState} from "@/store/states/IOrderState.interface";

// @ts-ignore
import TransportationComponent from "../../components/Forms/Transportation/TransportationComponent.vue";
// @ts-ignore
import TransportationTicketComponent from "../../components/transportationTicket/TransportationTicketComponent.vue";
// @ts-ignore
import TransportationItemComponent from "../../components/transportationItem/TransportationItemComponent.vue";
// @ts-ignore
import CheckBoxComponent from "../../components/CheckBox/CheckBoxComponent.vue";
// @ts-ignore
import DatesSliderComponent from "../../components/datesSlider/DatesSliderComponent.vue";
// @ts-ignore
import AdditionalInfoCheckboxModal from "../../components/tripAdditionalInfoCheckboxModal/tripAdditionalInfoCheckboxModalComponent.vue";

import {dateHelpers} from "../../helpers/dateHelpers";
import type {ITransportationsRequestQueryParams} from "@/interfaces/IRequestQueryParams.interface";
import type { IApiResponseTransportation } from "@/interfaces/apiResponseInterfaces/IApiResponseTransportation.interface";



export default defineComponent({
  name: 'RoundTripFirstStepPage',
  components: {
    TransportationItemComponent,
    CheckBoxComponent,
    DatesSliderComponent,
    TransportationTicketComponent,
    TransportationComponent,
    AdditionalInfoCheckboxModal
  },

  setup() {
    const store = useStore();

    return {
      store
    }
  },

  data() {
    return {
      mapCenter: <Coords>{},
      backEndErrorMessage: <string[]>[],
      transportationsMessage: <string[]>[],
      isLoading: false,
    }
  },

  computed: {
    ...mapState<IOrderState>('order', {
      roundTrip: (state: IOrderState) => state.roundTrip,
    }),
    ...mapState<IOrderTrueRoundTripState>('orderTrueRoundTrip', {
      pickUp: (state: IOrderTrueRoundTripState) => state.pickUp,
      dropOff: (state: IOrderTrueRoundTripState) => state.dropOff,
      tripDepartureDate: (state: IOrderTrueRoundTripState) => state.tripDepartureDate,
      transportations: (state: IOrderTrueRoundTripState) => state.transportations,
      forwardTicket: (state: IOrderTrueRoundTripState) => state.forwardTicket,
      backTicket: (state: IOrderTrueRoundTripState) => state.backTicket,
    }),

    tripObject(): ITransportationsRequestQueryParams {
      const state = this.store.state.orderTrueRoundTrip;

      return {
        'pickup': state.pickUp.id,
        'drop-off': state.dropOff.id,
        'pickup-date': dateHelpers.dateToDisplay(state.tripDepartureDate),
        'drop-off-date': dateHelpers.dateToDisplay(state.tripArrivalDate),
        'passengers': state.tripPassengers
      }
    },

    specifiedForwardTicketData() {
      const state = this.store.state.orderTrueRoundTrip;

      let params = {
        'pickup_specified_address': state.forwardTicket.pickup_specified_address,
        'dropoff_specified_address': state.forwardTicket.dropoff_specified_address,
        'pickup_note': state.forwardTicket.pickup_note,
        'dropoff_note': state.forwardTicket.dropoff_note,
        'pickup_is_checked': (state.forwardTicket.pickup_specified_address.length > 0) || (state.forwardTicket.pickup_note.length > 0),
        'dropoff_is_checked': (state.forwardTicket.dropoff_specified_address.length > 0) || (state.forwardTicket.dropoff_note.length > 0)
      }

      return params;
    },
  },

  methods: {
    isRoundTrip: function ($event: {target: HTMLInputElement}): void {
      this.store.dispatch('order/updateInitStates', [{prop: 'roundTrip', value: $event.target.checked}]);

      this.$router.push(routesConst.TRIPS);
    },

    onSelectDate($event: {date: string}): void {
      this.store.dispatch('orderTrueRoundTrip/updateState', [{prop: 'tripDepartureDate', value: new Date($event.date)}]);
      this.onSearchSubmit();
    },

    onSelectTransportation($event: {transportationId: number}): void {
      const state = this.store.state.orderTrueRoundTrip;
      this.store.dispatch('orderTrueRoundTrip/updateState', [{
        prop: 'forwardTicket',
        value: {
          transportationId: $event.transportationId,
          isSelected: true,
          data: state.transportations.find(trip => trip.id === $event.transportationId),
          tripDate: new Date(state.tripDepartureDate),
          pickUp: state.pickUp,
          dropOff: state.dropOff,
          pickup_specified_address: state.forwardTicket.pickup_specified_address,
          dropoff_specified_address: state.forwardTicket.dropoff_specified_address,
          pickup_note: state.forwardTicket.pickup_note,
          dropoff_note: state.forwardTicket.dropoff_note
        }
      }]);

      this.$router.push(routesConst.ROUND_TRIP_SECOND_STEP);
    },

    onChangeAddress(event: {id: string, value: string}): void {
        let forwardTicket = this.store.state.orderTrueRoundTrip.forwardTicket;
        switch (event.id) {
            case 'additional-pickup-info':
                forwardTicket.pickup_specified_address = event.value;

                break;
            case 'additional-dropoff-info':
                forwardTicket.dropoff_specified_address = event.value;
                
                break;
        };

        this.store.dispatch('orderTrueRoundTrip/updateState', [{prop: 'forwardTicket', value: forwardTicket}]);
    },

    onChangeNote(event: {id: string, value: string}): void {
        let forwardTicket = this.store.state.orderTrueRoundTrip.forwardTicket;
        switch (event.id) {
            case 'additional-pickup-info':
                forwardTicket.pickup_note = event.value;

                break;
            case 'additional-dropoff-info':
                forwardTicket.dropoff_note = event.value;
                
                break;
        };

        this.store.dispatch('orderTrueRoundTrip/updateState', [{prop: 'forwardTicket', value: forwardTicket}]);
    },

    onSearchSubmit(): void {
      this.store.commit('orderTrueRoundTrip/INITIALIZE_TRUE_ROUND_TRIP_STATE');

      this.backEndErrorMessage = this.transportationsMessage = [];
      this.isLoading = true;

      apiTransportationsService.getTransportations(this.tripObject)
          .then(response => {
            let isError = false;
            if (!response.data!.length) {
              this.transportationsMessage.push('transportations_not_found');
              isError = true;
            }

            if (response.errors!.length) {
              this.backEndErrorMessage = [...<[]>response.errors];
              isError = true;
            }

            if (isError) {
              return;
            }

            let rawTransportations: IApiResponseTransportation[]|null = response.data;

            // @ts-ignore
            const data = rawTransportations.map((item) => {
              if (item.hasOwnProperty('map_response') && item['map_response']) {
                let map_response: any = null;
                try {
                  map_response = JSON.parse(item['map_response']);

                  if (Array.isArray(map_response)) {
                    map_response = apiGoogleService.deserializeRoute(map_response[0]);
                    item = { ...item, map_response };
                  } else {
                    console.error('Got map response but it\'s not an array as it supposed to');
                  }

                } catch (Exception) {
                  console.error(Exception);
                }
              }

              return item;
            });

            this.store.dispatch('orderTrueRoundTrip/updateState', [{prop: 'transportations', value: data}]);

            this.isLoading = false;
          });
    }
  },

  created() {
    const state = this.store.state.orderTrueRoundTrip;

    // if (state.forwardTicket.pickUp && state.forwardTicket.dropOff) {
    //   this.store.dispatch('orderTrueRoundTrip/updateState', [
    //     {
    //       prop: 'pickUp',
    //       value: state.forwardTicket.pickUp
    //     },
    //     {
    //       prop: 'dropOff',
    //       value: state.forwardTicket.dropOff
    //     }
    //   ]);
    // }

    this.onSearchSubmit();

    this.mapCenter = apiGoogleService.COUNTRY_CENTER_COORDINATES_LT;

    axios.get(apiService.pageRoundTripFirstStepPageEndpoint())
        .then((response) => {
          // ...
        });
  },

  mounted() {
    //
  }
})
</script>

<style scoped lang="scss">
@import '../../pages/tripsPage/trips.scss';
</style>